/**
 * Lightweight two-language (nl/en) i18n for the All-e recommender.
 *
 * Mechanism: Dutch-string-as-key. All JSX keeps its Dutch copy inline and
 * wraps it in t("..."); the EN map in ./en.ts translates. When lang === "nl"
 * t() returns the key unchanged, so a missing wrap or missing EN entry fails
 * soft (Dutch shows instead of a broken key).
 *
 * Language state priority on load: ?lang=en URL param > localStorage > "nl".
 * Changing language persists to localStorage, mirrors into the URL (shareable
 * links) and updates <html lang>.
 */

/// <reference types="vite/client" />
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";

import { EN } from "./en";

export type Lang = "nl" | "en";

const STORAGE_KEY = "alle-check-lang";

export function translate(lang: Lang, s: string): string {
  if (lang === "nl") return s;
  const hit = EN[s];
  if (hit === undefined && import.meta.env.DEV) {
    console.warn(`[i18n] missing EN translation: "${s}"`);
  }
  return hit ?? s;
}

/** Intl locale matching the active language (en-GB: "12 June 2026", point decimals). */
export function localeOf(lang: Lang): string {
  return lang === "en" ? "en-GB" : "nl-NL";
}

function readInitialLang(): Lang {
  if (typeof window === "undefined") return "nl";
  const param = new URLSearchParams(window.location.search).get("lang");
  if (param === "en" || param === "nl") return param;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "en" || stored === "nl") return stored;
  return "nl";
}

interface LangContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
}

const LangContext = createContext<LangContextValue>({ lang: "nl", setLang: () => {} });

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => readInitialLang());

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    setLangState(next);
    if (typeof window === "undefined") return;
    window.localStorage.setItem(STORAGE_KEY, next);
    const url = new URL(window.location.href);
    if (next === "en") url.searchParams.set("lang", "en");
    else url.searchParams.delete("lang");
    window.history.replaceState(null, "", url.toString());
  }, []);

  return <LangContext.Provider value={{ lang, setLang }}>{children}</LangContext.Provider>;
}

export function useLang(): LangContextValue {
  return useContext(LangContext);
}

/** Returns the active translate function: t("Dutch source string") -> display string. */
export function useT(): (s: string) => string {
  const { lang } = useContext(LangContext);
  return useCallback((s: string) => translate(lang, s), [lang]);
}
